import { buildApiUrl, getApiBaseCandidates } from "@/lib/apiBase";

export type BackendUserRole = "lessee" | "lessor" | "agent" | "provider" | "oem" | "admin" | "accountant" | "user";

export interface BackendAuthUser {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  companyName?: string | null;
  role: BackendUserRole;
  isVerified?: boolean;
  isActive?: boolean;
  referredBy?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthResponse {
  success: boolean;
  message: string;
  token: string;
  user: BackendAuthUser;
}

interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  phone?: string;
  companyName?: string;
  role?: BackendUserRole;
  referredBy?: string;
}

interface MessageResponse {
  success: boolean;
  message: string;
}

let preferredAuthApiBase = "";
const AUTH_GET_CACHE_TTL_MS = 5000;
const authGetRequestCache = new Map<string, { expiresAt: number; promise: Promise<Record<string, unknown>> }>();

const readResponse = async <T extends Record<string, unknown>>(response: Response) => {
  let payload: Record<string, unknown> = {};

  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    throw new Error(String(payload.message || "Authentication request failed."));
  }

  return payload as T;
};

const buildAuthCacheKey = (route: string, init?: RequestInit) => {
  const method = String(init?.method || "GET").toUpperCase();
  const headers = new Headers(init?.headers);
  return `${method}:${route}:${headers.get("Authorization") || "anonymous"}`;
};

const authRequest = async <T extends Record<string, unknown>>(route: string, init?: RequestInit) => {
  const method = String(init?.method || "GET").toUpperCase();
  const cacheKey = buildAuthCacheKey(route, init);
  const isCacheableGet = method === "GET";

  if (isCacheableGet) {
    const cached = authGetRequestCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.promise as Promise<T>;
    }
  } else {
    authGetRequestCache.clear();
  }

  const requestPromise = (async () => {
  const bases = getApiBaseCandidates(preferredAuthApiBase);
  let lastError: Error | null = null;

  for (const base of bases) {
    try {
      const response = await fetch(buildApiUrl(`/api/auth${route}`, base), init);

      if (response.status === 404 && base !== bases[bases.length - 1]) {
        lastError = new Error("Authentication route not found.");
        continue;
      }

      const payload = await readResponse<T>(response);
      preferredAuthApiBase = base;
      return payload;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error("Authentication request failed.");
    }
  }

  throw lastError || new Error("Authentication request failed.");
  })();

  if (isCacheableGet) {
    authGetRequestCache.set(cacheKey, {
      expiresAt: Date.now() + AUTH_GET_CACHE_TTL_MS,
      promise: requestPromise as Promise<Record<string, unknown>>,
    });
    requestPromise.catch(() => {
      authGetRequestCache.delete(cacheKey);
    });
  }

  return requestPromise;
};

export const loginAccount = async (email: string, password: string) => authRequest<AuthResponse>("/login", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ email, password }),
});

export const registerAccount = async (payload: RegisterPayload) => authRequest<AuthResponse>("/register", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify(payload),
});

export const requestPasswordReset = async (email: string) => authRequest<MessageResponse>("/forgot-password", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ email }),
});

export const resetPasswordWithToken = async (token: string, password: string) => authRequest<MessageResponse>("/reset-password", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ token, password }),
});

export const fetchMyProfile = async (token: string) => authRequest<AuthResponse>("/me", {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const fetchAdminUsers = async (token: string) => authRequest<{ success: boolean; users: BackendAuthUser[] }>("/users", {
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const updateAdminUserRole = async (token: string, userId: number, role: BackendUserRole) => authRequest<AuthResponse>(`/users/${userId}/role`, {
  method: "PATCH",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify({ role }),
});

export const updateAdminUserAccess = async (token: string, userId: number, payload: { isVerified?: boolean; isActive?: boolean }) => authRequest<AuthResponse>(`/users/${userId}/access`, {
  method: "PATCH",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify(payload),
});

export const updateAdminUserPassword = async (token: string, userId: number, password: string) => authRequest<AuthResponse>(`/users/${userId}/password`, {
  method: "PATCH",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify({ password }),
});

export const deleteAdminUser = async (token: string, userId: number) => authRequest<{ success: boolean; message: string }>(`/users/${userId}`, {
  method: "DELETE",
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

export const adminSetUserKycStatus = async (token: string, userId: number, kycStatus: "approved" | "rejected" | "not-started") => authRequest<AuthResponse>(`/users/${userId}/kyc`, {
  method: "PATCH",
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
  body: JSON.stringify({ kycStatus }),
});
